import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import './App.css';

// Types
interface Element {
  symbol: string;
  name: string;
  number: number;
  mass: string;
  color: string;
  rgb: string;
}

// Element Data
const elements: Element[] = [
  { symbol: 'U', name: 'Uranium', number: 92, mass: '238.03', color: '#39ff14', rgb: '57, 255, 20' },
  { symbol: 'P', name: 'Phosphorus', number: 15, mass: '30.974', color: '#00ffff', rgb: '0, 255, 255' },
  { symbol: 'O', name: 'Oxygen', number: 8, mass: '15.999', color: '#4d9fff', rgb: '77, 159, 255' },
  { symbol: 'N', name: 'Nitrogen', number: 7, mass: '14.007', color: '#ff00ff', rgb: '255, 0, 255' },
  { symbol: 'Li', name: 'Lithium', number: 3, mass: '6.941', color: '#ff1493', rgb: '255, 20, 147' },
];

// Components
const Particle: React.FC<{ delay: number; color: string }> = ({ delay, color }) => {
  const left = Math.random() * 100;
  const duration = 15 + Math.random() * 10;
  
  return (
    <motion.div
      className="particle"
      style={{
        left: `${left}%`,
        background: color,
      }}
      initial={{ y: '100vh', opacity: 0, scale: 0 }}
      animate={{ 
        y: '-100px', 
        opacity: [0, 0.3, 0.3, 0],
        scale: 1 
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    />
  );
};

const ElementCard: React.FC<{ element: Element; index: number }> = ({ element, index }) => {
  const [isFlickering, setIsFlickering] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() > 0.7) {
        setIsFlickering(true);
        setTimeout(() => setIsFlickering(false), 150);
      }
    }, 2000 + index * 500);
    return () => clearInterval(interval);
  }, [index]);

  return (
    <motion.div
      className="element"
      style={{
        borderColor: element.color,
        ['--element-color' as string]: element.color,
        ['--element-rgb' as string]: element.rgb,
      }}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.15, duration: 0.6 }}
      whileHover={{ 
        y: -8,
        boxShadow: `0 15px 50px rgba(${element.rgb}, 0.4)`,
      }}
    >
      <span className="element-number">{element.number}</span>
      <motion.span
        className="element-symbol"
        style={{
          color: element.color,
          textShadow: `0 0 10px ${element.color}, 0 0 20px ${element.color}, 0 0 40px ${element.color}`,
        }}
        animate={{
          opacity: isFlickering ? [1, 0.3, 0.9, 0.2, 1] : [1, 0.8, 1],
          filter: isFlickering ? 'brightness(1.5)' : ['brightness(1)', 'brightness(1.3)', 'brightness(1)'],
        }}
        transition={{
          duration: isFlickering ? 0.15 : 2,
          repeat: isFlickering ? 0 : Infinity,
          ease: 'easeInOut',
        }}
      >
        {element.symbol}
      </motion.span>
      <span className="element-name">{element.name}</span>
      <span className="element-mass">{element.mass}</span>
    </motion.div>
  );
};

// SVG Icons
const TelegramIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const XIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const Navigation: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <motion.nav
      className={`nav ${scrolled ? 'scrolled' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="logo">$UPONLi</div>
      <ul className="nav-links">
        {['about', 'utility', 'tokenomics', 'roadmap', 'buy'].map((item) => (
          <li key={item}>
            <button onClick={() => scrollTo(item)}>{item.toUpperCase()}</button>
          </li>
        ))}
      </ul>
      <div className="social-links">
        <a href="https://x.com/UponLiEth" target="_blank" rel="noopener noreferrer" title="X (Twitter)"><XIcon /></a>
        <a href="https://t.me/UpOnliPortal" target="_blank" rel="noopener noreferrer" title="Telegram"><TelegramIcon /></a>
      </div>
    </motion.nav>
  );
};

// Glowing Chemical Formula Component
const ChemicalFormula: React.FC = () => {
  return (
    <motion.div 
      className="chemical-formula"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.6, duration: 0.8 }}
    >
      {elements.map((el, i) => (
        <motion.span
          key={el.symbol}
          className="formula-element"
          style={{ 
            color: el.color,
            textShadow: `0 0 10px ${el.color}, 0 0 20px ${el.color}, 0 0 40px ${el.color}`,
          }}
          animate={{
            textShadow: [
              `0 0 10px ${el.color}, 0 0 20px ${el.color}, 0 0 40px ${el.color}`,
              `0 0 20px ${el.color}, 0 0 40px ${el.color}, 0 0 60px ${el.color}`,
              `0 0 10px ${el.color}, 0 0 20px ${el.color}, 0 0 40px ${el.color}`,
            ],
          }}
          transition={{
            duration: 2,
            delay: i * 0.2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          {el.symbol}
        </motion.span>
      ))}
      <motion.span 
        className="formula-arrow"
        animate={{ 
          x: [0, 10, 0],
          opacity: [0.5, 1, 0.5],
        }}
        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
      >
        →
      </motion.span>
      <motion.span 
        className="formula-result"
        animate={{
          textShadow: [
            '0 0 10px #39ff14, 0 0 20px #39ff14',
            '0 0 30px #39ff14, 0 0 50px #39ff14, 0 0 70px #39ff14',
            '0 0 10px #39ff14, 0 0 20px #39ff14',
          ],
          scale: [1, 1.05, 1],
        }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      >
        UP ONLY ↑
      </motion.span>
    </motion.div>
  );
};

const Hero: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const contractAddress = '0xe09Df60Af34b3e91DF35fAF846Fe79e1c3420ca6';

  const copyContract = async () => {
    await navigator.clipboard.writeText(contractAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section className="hero">
      <ChemicalFormula />
      
      <div className="elements-container">
        {elements.map((element, index) => (
          <ElementCard key={element.symbol} element={element} index={index} />
        ))}
      </div>

      <motion.h1
        className="hero-title"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.6 }}
      >
        The Elements of Gains
      </motion.h1>

      <motion.p
        className="hero-subtitle"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.6 }}
      >
        A perfectly balanced formula engineered for one purpose: <span className="highlight">UP ONLY</span>. 
        Five elements. One direction. Infinite potential.
      </motion.p>

      <motion.div
        className="cta-buttons"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.6 }}
      >
        <motion.button 
          className="btn btn-primary" 
          onClick={() => document.getElementById('buy')?.scrollIntoView({ behavior: 'smooth' })}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Buy $UPONLi
        </motion.button>
        <motion.a 
          href="#" 
          className="btn btn-secondary"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Chart
        </motion.a>
      </motion.div>

      <motion.div
        className="contract-display"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.4, duration: 0.6 }}
        whileHover={{ scale: 1.02 }}
      >
        <span className="contract-label">CONTRACT:</span>
        <span className="contract-address">{contractAddress}</span>
        <motion.button 
          className="copy-btn" 
          onClick={copyContract}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {copied ? '✓ COPIED!' : 'COPY'}
        </motion.button>
      </motion.div>
    </section>
  );
};

const About: React.FC = () => {
  return (
    <section id="about" className="section">
      <motion.div
        className="section-header"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="section-title">The Formula</h2>
        <p className="section-subtitle">Synthesized for maximum gains</p>
      </motion.div>

      <div className="about-content">
        {[
          {
            title: '[ COMPOUND ANALYSIS ]',
            content: `$UPONLi represents the perfect synthesis of elemental forces: the radioactive power of Uranium, 
            the luminescent energy of Phosphorus, the life-giving essence of Oxygen, the atmospheric 
            abundance of Nitrogen, and the reactive potential of Lithium. Together, they form an 
            unstoppable compound with only one trajectory.`
          },
          {
            title: '[ REACTION MECHANICS ]',
            content: `When market participants interact with $UPONLi, a chain reaction initiates. Buy pressure 
            catalyzes upward momentum. Sell pressure triggers automated buybacks, converting negative 
            force into positive energy. The result? A self-sustaining reaction that defies traditional 
            market physics. The formula is simple: UP ONLY.`
          }
        ].map((card, index) => (
          <motion.div
            key={card.title}
            className="about-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.2, duration: 0.6 }}
          >
            <h3>{card.title}</h3>
            <p>{card.content}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

// Mini Element Icon Component for Utility Cards
const MiniElement: React.FC<{ symbol: string; color: string; rgb: string }> = ({ symbol, color, rgb }) => {
  const [isFlickering, setIsFlickering] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() > 0.6) {
        setIsFlickering(true);
        setTimeout(() => setIsFlickering(false), 150);
      }
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div
      className="mini-element"
      style={{
        borderColor: color,
        ['--element-color' as string]: color,
        ['--element-rgb' as string]: rgb,
      }}
      animate={{
        boxShadow: isFlickering 
          ? [`0 0 20px rgba(${rgb}, 0.8)`, `0 0 5px rgba(${rgb}, 0.2)`, `0 0 30px rgba(${rgb}, 1)`]
          : [`0 0 15px rgba(${rgb}, 0.4)`, `0 0 25px rgba(${rgb}, 0.6)`, `0 0 15px rgba(${rgb}, 0.4)`],
      }}
      transition={{
        duration: isFlickering ? 0.15 : 2,
        repeat: isFlickering ? 0 : Infinity,
        ease: 'easeInOut',
      }}
    >
      <motion.span
        className="mini-element-symbol"
        style={{
          color: color,
          textShadow: `0 0 10px ${color}, 0 0 20px ${color}, 0 0 40px ${color}`,
        }}
        animate={{
          opacity: isFlickering ? [1, 0.2, 0.8, 0.3, 1] : [1, 0.85, 1],
          filter: isFlickering ? 'brightness(1.8)' : ['brightness(1)', 'brightness(1.4)', 'brightness(1)'],
        }}
        transition={{
          duration: isFlickering ? 0.15 : 2,
          repeat: isFlickering ? 0 : Infinity,
          ease: 'easeInOut',
        }}
      >
        {symbol}
      </motion.span>
    </motion.div>
  );
};

const Utility: React.FC = () => {
  const utilities = [
    { 
      element: { symbol: 'U', color: '#39ff14', rgb: '57, 255, 20' },
      title: 'Buy = Up', 
      desc: 'Every purchase adds positive pressure to the compound, driving momentum in the only direction that matters.' 
    },
    { 
      element: { symbol: 'P', color: '#00ffff', rgb: '0, 255, 255' },
      title: 'Sell = Up', 
      desc: 'Automated buyback mechanisms convert sell pressure into buy pressure. Sellers fuel the reaction.' 
    },
    { 
      element: { symbol: 'Li', color: '#ff1493', rgb: '255, 20, 147' },
      title: 'Hold = Up', 
      desc: 'Diamond hands reduce circulating supply while the reaction continues. Time amplifies the formula.' 
    },
  ];

  return (
    <section id="utility" className="section">
      <motion.div
        className="section-header"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="section-title">Utility</h2>
        <p className="section-subtitle">Engineered for one outcome</p>
      </motion.div>

      <div className="utility-grid">
        {utilities.map((item, index) => (
          <motion.div
            key={item.title}
            className="utility-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.15, duration: 0.6 }}
            whileHover={{ 
              y: -8,
              borderColor: item.element.color,
              boxShadow: `0 15px 50px rgba(${item.element.rgb}, 0.25)`,
            }}
          >
            <MiniElement symbol={item.element.symbol} color={item.element.color} rgb={item.element.rgb} />
            <h3>{item.title}</h3>
            <p>{item.desc}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

const Tokenomics: React.FC = () => {
  const stats = [
    { value: '1T', label: 'Total Supply' },
    { value: '0%', label: 'Team Allocation' },
    { value: '100%', label: 'LP Locked' },
    { value: '∞', label: 'Potential' },
  ];

  return (
    <section id="tokenomics" className="section">
      <motion.div
        className="section-header"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="section-title">Tokenomics</h2>
        <p className="section-subtitle">The molecular breakdown</p>
      </motion.div>

      <div className="tokenomics-container">
        <div className="tokenomics-grid">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              className="tokenomics-item"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{
                scale: 1.05,
                borderColor: 'rgba(57, 255, 20, 0.5)',
                boxShadow: '0 10px 40px rgba(57, 255, 20, 0.2)',
              }}
            >
              <motion.div 
                className="tokenomics-value"
                animate={{
                  textShadow: [
                    '0 0 20px rgba(57, 255, 20, 0.5)',
                    '0 0 40px rgba(57, 255, 20, 0.8)',
                    '0 0 20px rgba(57, 255, 20, 0.5)',
                  ],
                }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              >
                {stat.value}
              </motion.div>
              <div className="tokenomics-label">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Roadmap: React.FC = () => {
  const phases = [
    { phase: 'Phase 01', title: 'Genesis Reaction', desc: 'Contract deployment, website launch, social channels activation. The elements combine.' },
    { phase: 'Phase 02', title: 'Chain Reaction', desc: 'Community growth, marketing campaigns, influencer partnerships. The reaction spreads.' },
    { phase: 'Phase 03', title: 'Critical Mass', desc: 'CEX listings, expanded utility features, global recognition. The compound reaches critical mass.' },
    { phase: 'Phase 04', title: 'Fusion', desc: 'Full ecosystem development, strategic partnerships, market dominance. Elements fuse into permanence.' },
  ];

  return (
    <section id="roadmap" className="section">
      <motion.div
        className="section-header"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="section-title">Roadmap</h2>
        <p className="section-subtitle">The reaction sequence</p>
      </motion.div>

      <div className="roadmap-container">
        <div className="roadmap-line" />
        {phases.map((item, index) => (
          <motion.div
            key={item.phase}
            className={`roadmap-item ${index % 2 === 0 ? 'left' : 'right'}`}
            initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.2, duration: 0.6 }}
          >
            <div className="roadmap-content">
              <div className="roadmap-phase">{item.phase}</div>
              <h3 className="roadmap-title">{item.title}</h3>
              <p className="roadmap-desc">{item.desc}</p>
            </div>
            <div className="roadmap-dot" />
          </motion.div>
        ))}
      </div>
    </section>
  );
};

const HowToBuy: React.FC = () => {
  const steps = [
    { num: 1, title: 'Create a Wallet', desc: 'Download MetaMask or your preferred Web3 wallet. Secure your seed phrase in a safe location.' },
    { num: 2, title: 'Get ETH', desc: 'Purchase Ethereum from an exchange and transfer it to your wallet address.' },
    { num: 3, title: 'Connect to Uniswap', desc: 'Navigate to app.uniswap.org and connect your wallet to the Ethereum network.' },
    { num: 4, title: 'Swap for $UPONLi', desc: 'Paste the contract address, set slippage to 6%, and execute the swap. Welcome to the reaction.' },
  ];

  return (
    <section id="buy" className="section">
      <motion.div
        className="section-header"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="section-title">How to Buy</h2>
        <p className="section-subtitle">Join the reaction</p>
      </motion.div>

      <div className="buy-steps">
        {steps.map((step, index) => (
          <motion.div
            key={step.num}
            className="buy-step"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.15, duration: 0.5 }}
            whileHover={{ borderColor: '#39ff14' }}
          >
            <div className="step-number">{step.num}</div>
            <div className="step-content">
              <h3>{step.title}</h3>
              <p>{step.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

const Footer: React.FC = () => {
  return (
    <footer className="footer">
      <motion.div
        className="footer-logo"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        $UPONLi
      </motion.div>
      <div className="footer-socials">
        <motion.a 
          href="https://x.com/UponLiEth" 
          target="_blank" 
          rel="noopener noreferrer"
          whileHover={{ 
            borderColor: '#39ff14', 
            color: '#39ff14',
            boxShadow: '0 0 25px rgba(57, 255, 20, 0.4)',
          }}
        >
          <XIcon />
        </motion.a>
        <motion.a 
          href="https://t.me/UpOnliPortal" 
          target="_blank" 
          rel="noopener noreferrer"
          whileHover={{ 
            borderColor: '#39ff14', 
            color: '#39ff14',
            boxShadow: '0 0 25px rgba(57, 255, 20, 0.4)',
          }}
        >
          <TelegramIcon />
        </motion.a>
      </div>
    </footer>
  );
};

// Main App
const App: React.FC = () => {
  const particleColors = ['#39ff14', '#00ffff', '#4d9fff', '#ff00ff', '#ff1493'];

  return (
    <div className="App">
      {/* Background Effects */}
      <div className="grid-bg" />
      <motion.div
        className="scan-line"
        animate={{ top: ['0%', '100%'] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
      />
      <div className="particles">
        {Array.from({ length: 25 }).map((_, i) => (
          <Particle
            key={i}
            delay={Math.random() * 15}
            color={particleColors[Math.floor(Math.random() * particleColors.length)]}
          />
        ))}
      </div>

      <Navigation />
      <Hero />
      <About />
      <Utility />
      <Tokenomics />
      <Roadmap />
      <HowToBuy />
      <Footer />
    </div>
  );
};

export default App;
